import { watchImmediate } from "@vueuse/core";
import { ref } from "vue";
import { toRemString } from "../../../utils/css";

/**
 * Compute a short hand value for the box model.
 * @param {Number} top
 * @param {Number} right
 * @param {Number} bottom
 * @param {Number} left
 * @returns {String}
 *
 * @example
 * computeShortHand(1, 1, 4, 4) // "1rem 1rem 4rem 4rem"
 * computeShortHand(1, 1, 1, 1) // "1rem"
 * computeShortHand(1, 2, 3, 4) // "1rem 2rem 3rem 4rem"
 */
function computeShortHand(properties) {
  const { top, right, bottom, left } = properties;
  const values = [top, right, bottom, left].map((value) => value ?? 0);
  const uniqueValues = new Set(values);

  if (uniqueValues.size === 1) {
    return toRemString(uniqueValues.values().next().value);
  }

  return values.map(toRemString).join(" ");
}

/**
 * Use a box model property and compute a short hand value for it.
 *
 * @param {Object} model The box model properties to use.
 * @param {Number} model.top
 * @param {Number} model.right
 * @param {Number} model.bottom
 * @param {Number} model.left
 * @returns {Object}
 *
 * @example
 * const { top, right, bottom, left, shortHand } = useBoxModel({ top: 1, right: 1, bottom: 4, left: 4 });
 *
 * top.value = 2;
 * right.value = 2;
 * bottom.value = 8;
 */
export function useBoxModelUI(model) {
  const positions = ["top", "right", "bottom", "left"];
  const shortHand = ref("");

  watchImmediate(model, handleModelChange, {
    deep: true,
  });

  function handleModelChange(value) {
    if (model.value.locked) {
      updateAllPositions(value);
    }

    updateShortHand(value);
  }

  function updateShortHand(value) {
    shortHand.value = computeShortHand(value);
  }

  function updateAllPositions(value) {
    for (const position of positions) {
      model.value[position] = value.all;
    }
  }

  function setLocked(value) {
    model.value.locked = value;
  }

  return {
    shortHand,
    positions,

    setLocked,
  };
}
